import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  ChevronRight,
  X,
  Calendar,
  Flag
} from 'lucide-react';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useModules } from '@/contexts/ModulesContext';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const priorityColors = {
  high: 'border-l-red-500',
  medium: 'border-l-amber-500',
  low: 'border-l-green-500'
};

export default function TaskNotificationWidget() {
  const { user } = useAuth();
  const { isModuleEnabled } = useModules();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState({ pending: 0, review: 0, overdue: 0 });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin';
  const isModuleActive = isModuleEnabled('project_tasks');

  const fetchStats = useCallback(async () => {
    if (!isSuperAdmin) return;
    
    try {
      const response = await axios.get(`${API}/api/project-tasks/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching task stats:', error);
    }
  }, [isSuperAdmin]);

  const fetchTasks = useCallback(async () => {
    if (!isSuperAdmin) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/project-tasks`, getAuthHeaders());
      // Only show pending and review tasks, sorted by priority and deadline
      const activeTasks = response.data
        .filter(t => t.status !== 'completed')
        .slice(0, 5); // Limit to 5 tasks in widget
      setTasks(activeTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  useEffect(() => {
    if (isOpen) {
      fetchTasks();
    }
  }, [isOpen, fetchTasks]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/api/project-tasks/${taskId}/status`, { status: newStatus }, getAuthHeaders());
      fetchStats();
      fetchTasks();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const isOverdue = (deadline, status) => {
    if (!deadline || status === 'completed') return false;
    return isPast(parseISO(deadline)) && !isToday(parseISO(deadline));
  };

  const totalActive = stats.pending + stats.review;

  // Don't render if not superadmin or module is disabled
  if (!isSuperAdmin || !isModuleActive) return null;

  return (
    <>
      {/* Floating Badge Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-300 ${
          totalActive > 0 
            ? stats.overdue > 0 
              ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
              : 'bg-purple-600 hover:bg-purple-700'
            : 'bg-green-500 hover:bg-green-600'
        }`}
        data-testid="task-widget-button"
      >
        {totalActive > 0 ? (
          <>
            <CheckCircle className="w-6 h-6 text-white" />
            <span className="absolute -top-1 -right-1 w-6 h-6 bg-white text-purple-600 rounded-full text-xs font-bold flex items-center justify-center shadow">
              {totalActive > 99 ? '99+' : totalActive}
            </span>
          </>
        ) : (
          <CheckCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Sliding Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">Задачи проекта</h3>
              <p className="text-purple-200 text-sm">{totalActive} активных задач</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="flex border-b bg-gray-50">
          <div className="flex-1 p-3 text-center border-r">
            <div className="text-xl font-bold text-amber-600">{stats.pending}</div>
            <div className="text-xs text-gray-500">Ожидают</div>
          </div>
          <div className="flex-1 p-3 text-center border-r">
            <div className="text-xl font-bold text-blue-600">{stats.review}</div>
            <div className="text-xs text-gray-500">На проверку</div>
          </div>
          <div className="flex-1 p-3 text-center">
            <div className={`text-xl font-bold ${stats.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {stats.overdue}
            </div>
            <div className="text-xs text-gray-500">Просрочено</div>
          </div>
        </div>

        {/* Task List */}
        <div className="overflow-y-auto" style={{ height: 'calc(100% - 180px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <CheckCircle className="w-12 h-12 mb-3 text-green-400" />
              <p className="font-medium">Все задачи выполнены!</p>
              <p className="text-sm">Отличная работа</p>
            </div>
          ) : (
            <div className="divide-y">
              {tasks.map(task => (
                <div 
                  key={task.id} 
                  className={`p-4 border-l-4 ${priorityColors[task.priority]} ${
                    isOverdue(task.deadline, task.status) ? 'bg-red-50' : 'hover:bg-gray-50'
                  } transition-colors`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 text-sm line-clamp-2">{task.title}</h4>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          task.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {task.status === 'pending' ? 'Ожидает' : 'На проверку'}
                        </span>
                        {task.deadline && (
                          <span className={`text-xs flex items-center gap-1 ${
                            isOverdue(task.deadline, task.status) ? 'text-red-600 font-medium' : 'text-gray-500'
                          }`}>
                            <Calendar className="w-3 h-3" />
                            {format(parseISO(task.deadline), 'd MMM', { locale: ru })}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex flex-col gap-1">
                      {task.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(task.id, 'review')}
                          className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="На проверку"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                      )}
                      {task.status === 'review' && (
                        <button
                          onClick={() => handleStatusChange(task.id, 'completed')}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Выполнено"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
          <Button
            onClick={() => {
              setIsOpen(false);
              navigate('/admin');
              // Small delay to let navigation complete, then we'd need to switch to tasks tab
              setTimeout(() => {
                // Try to click on tasks tab if it exists
                const tasksTab = document.querySelector('[data-value="tasks"]');
                if (tasksTab) tasksTab.click();
              }, 500);
            }}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            Открыть все задачи
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
